import React from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { pxToRem } from '@withjoy/joykit/theme';
import { productOffersGridStyles } from './ExternalProductOffersGrid.styles';
import { useTranslation } from '@shared/core';
import { ExternalProductTile, ExternalProductOfferTileProps } from '../ExternalProductTile';
import { ExternalProductOffer } from '../../../../Catalog.types';

const ProductGridLayout: React.FC = ({ children }) => {
  return <Box __css={productOffersGridStyles}>{children}</Box>;
};

type SearchResultsGridProps = {
  loading: boolean;
  offers: ExternalProductOffer[] | null;
  renderOffer: (props: ExternalProductOfferTileProps) => JSX.Element;
  hideHeader: boolean;
  isMobile: boolean;
};

type SearchResultsGridRootProps = {
  offers: ExternalProductOffer[];
  renderOffer: (props: ExternalProductOfferTileProps) => JSX.Element;
  isMobile: boolean;
};

const Root = ({ offers, renderOffer, isMobile }: SearchResultsGridRootProps) => {
  return (
    <ProductGridLayout>
      {offers.map((offer: ExternalProductOffer, index: number) =>
        renderOffer({
          index,
          isMobile,
          offer,
        }),
      )}
    </ProductGridLayout>
  );
};

const SKELETON_ARRAY = Array.from(Array(8));

const Skeleton = () => {
  return (
    <ProductGridLayout>
      {SKELETON_ARRAY.map((_, idx) => (
        <ExternalProductTile.Skeleton key={idx} />
      ))}
    </ProductGridLayout>
  );
};

const Header = () => {
  const { t } = useTranslation('catalogRegistry');

  const trans = t('universalSearch', 'productgrid');

  return (
    <Flex
      alignItems="center"
      flexDirection="column"
      rowGap={5}
    >
      <TextV2
        typographyVariant="Hed56"
        textAlign="center"
        marginTop={pxToRem(128)}
      >
        {trans.sectionHed()}
      </TextV2>
      <TextV2 typographyVariant="hed4">{trans.sectionDek()}</TextV2>
    </Flex>
  );
};

export const ExternalProductOffersGrid = (props: SearchResultsGridProps) => {
  const { offers, hideHeader, loading, isMobile, renderOffer } = props;

  if (!loading && !offers?.length) {
    return null;
  }

  return (
    <Flex flexDirection="column">
      {!hideHeader && <Header />}
      <SkeletonGroup
        isReady={!loading}
        marginBottom={pxToRem(131)}
        placeholder={<Skeleton />}
      >
        <Box
          display="flex"
          flexDirection="column"
        >
          {offers && (
            <Root
              offers={offers}
              isMobile={isMobile}
              renderOffer={renderOffer}
            />
          )}
        </Box>
      </SkeletonGroup>
    </Flex>
  );
};
